import { PlatformApiClient, PlatformApiError } from "./api";

const ORGANIZATION_ID = /^cd_org_[a-f0-9]{32}$/;

export interface MetaWhatsAppReviewStatus {
  enabled: boolean;
  sendEnabled: boolean;
  readyForWebhookVerification: boolean;
  readyForInbound: boolean;
  readyForGraph: boolean;
  readyForOutbound: boolean;
  organizationConfigured: boolean;
  wabaConfigured: boolean;
  phoneNumberConfigured: boolean;
  graphVersion: string | null;
  allowedDestinationTails: readonly string[];
}

export interface MetaWhatsAppReviewEvent {
  id: string;
  organizationId: string;
  direction: "inbound" | "outbound";
  providerMessageId: string | null;
  phoneNumberId: string;
  counterpartyTail: string;
  profileName: string | null;
  messageText: string;
  providerTimestampSeconds: number | null;
  createdAt: string;
}

interface ErrorBody {
  error?: { code?: string; message?: string };
  requestId?: string;
}

interface DataBody<T> extends ErrorBody {
  data?: T;
}

type Fetcher = typeof fetch;

export class MetaWhatsAppReviewApi {
  private readonly fetcher: Fetcher;

  constructor(
    private readonly platformApi: PlatformApiClient,
    fetcher: Fetcher = fetch,
  ) {
    this.fetcher = fetcher.bind(globalThis);
  }

  status(organizationId: string): Promise<{ status: MetaWhatsAppReviewStatus }> {
    return this.request(organizationId, "/status");
  }

  events(organizationId: string, limit = 50): Promise<{ events: readonly MetaWhatsAppReviewEvent[] }> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
      throw new PlatformApiError(400, "meta_review_limit_invalid", "The review event limit is invalid.");
    }
    return this.request(organizationId, `/events?limit=${limit}`);
  }

  phoneNumbers(organizationId: string): Promise<{ provider: unknown }> {
    return this.request(organizationId, "/phone-numbers");
  }

  templates(organizationId: string, limit = 25): Promise<{ provider: unknown }> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 100) {
      throw new PlatformApiError(400, "meta_review_limit_invalid", "The template limit is invalid.");
    }
    return this.request(organizationId, `/templates?limit=${limit}`);
  }

  createTemplate(organizationId: string, input: {
    name: string;
    language: string;
    category: "AUTHENTICATION" | "MARKETING" | "UTILITY";
    components: readonly Record<string, unknown>[];
  }): Promise<{ provider: unknown }> {
    return this.request(organizationId, "/templates", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  subscribe(organizationId: string): Promise<{ provider: unknown }> {
    return this.request(organizationId, "/subscribe", { method: "POST" });
  }

  send(organizationId: string, to: string, text: string): Promise<{
    provider: unknown;
    event: MetaWhatsAppReviewEvent;
  }> {
    return this.request(organizationId, "/send", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ to, text }),
    });
  }

  private async request<T>(
    organizationId: string,
    suffix: string,
    init: RequestInit = {},
  ): Promise<T> {
    if (!ORGANIZATION_ID.test(organizationId)) {
      throw new PlatformApiError(400, "organization_id_invalid", "The organization is invalid.");
    }
    if (!/^\/(?:status|events(?:\?limit=\d{1,3})?|phone-numbers|templates(?:\?limit=\d{1,3})?|subscribe|send)$/.test(suffix)) {
      throw new PlatformApiError(400, "meta_review_path_invalid", "The Meta review route is invalid.");
    }

    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      const security = await this.platformApi.refreshCsrf();
      headers.set("x-csrf-token", security.csrfToken);
    }

    const path = `/api/v1/platform/organizations/${encodeURIComponent(organizationId)}/integrations/meta/whatsapp/review${suffix}`;
    const response = await this.fetcher(path, {
      ...init,
      method,
      credentials: "include",
      headers,
    });
    const body = await response.json().catch(() => ({})) as DataBody<T>;
    if (!response.ok) {
      throw new PlatformApiError(
        response.status,
        body.error?.code ?? "meta_review_request_failed",
        body.error?.message ?? "WhatsApp review could not complete the request.",
        body.requestId ?? response.headers.get("x-request-id") ?? undefined,
      );
    }
    if (body.data === undefined) {
      throw new PlatformApiError(502, "meta_review_response_invalid", "WhatsApp review returned an invalid response.");
    }
    return body.data;
  }
}
